import { SetStoreFunction, createStore, reconcile } from 'solid-js/store';
import Room from '@model/api/response/Room';
import SignalRApi from '@root/api/signalRApi';
import Api from '@root/api/api';
import RouteBuilderService from './RouteBuilderService';
import RoomStorage from './RoomStorage';
import { Accessor, createMemo } from 'solid-js';
import RoomPlayer from '@model/api/response/RoomPlayer';

class SessionService {
    public roomId: string | null;
    public room: Room;
    private setRoom: SetStoreFunction<Room>;
    public selfPlayer: Accessor<RoomPlayer | undefined>;

    private routeBuilderService: RouteBuilderService;
    private signalRApi: SignalRApi;
    private api: Api;
    private roomStorage: RoomStorage;

    constructor(
        signalRApi: SignalRApi,
        routeBuilderService: RouteBuilderService,
        api: Api,
        roomStorage: RoomStorage,
    ) {
        this.roomId = null;
        const [room, setRoom] = createStore<Room>(this.createDefaultRoom(null));

        this.room = room;
        this.setRoom = setRoom;

        this.routeBuilderService = routeBuilderService;
        this.signalRApi = signalRApi;
        this.api = api;
        this.roomStorage = roomStorage;
        this.signalRApi.onRoomUpdated = this.onRoomUpdatedFromServer.bind(this);

        this.selfPlayer = createMemo(() =>
            this.room.players.find((x) => x.isSelf),
        );
    }

    public async joinSession(userName: string, roomId: string): Promise<void> {
        const room = await this.api.joinRoom(userName, roomId);
        await this.initSession(room);
    }

    public async startSession(userName: string): Promise<void> {
        const room = await this.api.createRoom(userName);
        await this.initSession(room);
    }

    public getJoinSessionLink() {
        const baseUrl = window.location.origin;
        return (
            baseUrl + this.routeBuilderService.buildRoomRoute(this.roomId ?? '')
        );
    }

    public async endSession(): Promise<void> {
        await this.signalRApi.disconnect();
    }

    public setRoomId(roomId: string): void {
        this.roomId = roomId;
        const room = this.roomStorage.getRoom(roomId);
        if (!room) {
            this.setRoom(this.createDefaultRoom(roomId));
            return;
        }

        this.setRoom(room);

        if (room.roomId !== '' && room.playerId !== '') {
            this.signalRApi.connect(room.roomId, room.playerId);
        }
    }

    private applyApiResponse(room: Room) {
        this.setRoom(reconcile(room));
        this.roomStorage.createOrUpdateRoom(room);
    }

    private async initSession(room: Room): Promise<void> {
        this.roomId = room.roomId;
        this.applyApiResponse(room);
        await this.signalRApi.connect(room.roomId, room.playerId);
    }

    private onRoomUpdatedFromServer(room: Room) {
        this.applyApiResponse(room);
    }

    private createDefaultRoom(roomId: string | null): Room {
        return {
            roomId: roomId ?? '',
            playerId: '',
            state: 'Join',
            playerNickname: '',
            players: [],
            ticketDescription: null,
            averageVote: null,
            voteDuration: null,
            roomNumber: 0,
        };
    }
}

export default SessionService;
